import React from 'react';
import AdSlot from '@gumtree/third-party-ads/src/components/ad-slot';
import { DisplayAdSlotDivNames } from '@gumtree/third-party-ads/src/framework/dfp/ad-config/slot-configs';

import Carousel, { Props as CarouselProps } from '@gumtree/ui-library/src/carousel/carousel';
import Dialog from '@gumtree/ui-library/src/dialog/dialog';
import { ImageSlide, SliderProps, VideoSlide } from '@gumtree/ui-library/src/carousel/slider';
import type { SlotName } from '@gumtree/third-party-ads/src/framework/dfp/ad-config/slot-configs';
import refreshGoogleAds from '@gumtree/third-party-ads/src/refresh-google-ads';
import type { WindowWithGoogleTag } from '@gumtree/shared/src/types/window';

import './index.scss';

export const extendSlidesWithAd = (
    slides: SliderProps['slides'],
    setIsShowGalleryBanner: React.Dispatch<boolean>
): SliderProps['slides'] => {
    if (slides.length >= 1) {
        const firstFewSlides = slides.slice(0, -1);
        const lastSlide = slides[slides.length - 1];
        const isSingleSlide = slides.length === 1;

        if (isSingleSlide) {
            lastSlide.onArrivalFromRight = () => {
                setIsShowGalleryBanner(true);
            };
        } else {
            lastSlide.onArrivalFromLeft = () => {
                // @ts-ignore
                refreshGoogleAds(window as WindowWithGoogleTag, [DisplayAdSlotDivNames.galleryMPU]);
            };
        }

        lastSlide.onArrivalFromRight = () => {
            setIsShowGalleryBanner(true);
        };

        return [
            ...firstFewSlides,
            lastSlide,
            {
                type: 'display-ad',
                slotId: DisplayAdSlotDivNames.galleryMPU as SlotName,
                onArrivalFromLeft: () => {
                    setIsShowGalleryBanner(false);

                    if (isSingleSlide) {
                        // @ts-ignore
                        refreshGoogleAds(window as WindowWithGoogleTag, [
                            DisplayAdSlotDivNames.galleryMPU,
                        ]);
                    }
                },
                invisibleToSlideCounter: true,
            },
        ];
    } else {
        return slides;
    }
};

const CarouselGallery = ({
    mediaLinks,
    hasControls,
    hasIcon,
    hasCounter,
    hasCounterDots,
    breakpoints,
    isDialogOpen,
    imageAltTitle,
    openDialog = () => {},
    slideNumber,
    closeDialog = () => {},
    endTracking,
    trackingContext,
}: Props) => {
    const [isShowGalleryBanner, setIsShowGalleryBanner] = React.useState(true);
    const slidesWithAd = extendSlidesWithAd(mediaLinks, setIsShowGalleryBanner);

    return (
        <div
            className="image-carousel carousel"
            data-q="image-carousel"
            data-testid="image-carousel"
        >
            <Dialog
                isOpen={isDialogOpen}
                onClose={() => {
                    closeDialog();
                }}
            >
                {isDialogOpen && (
                    <>
                        <Carousel
                            slides={slidesWithAd}
                            itemsToShow={1}
                            slideNumber={slideNumber}
                            imageAltTitle={imageAltTitle}
                            hasControls={hasControls}
                            hasIcon={hasIcon}
                            hasCounter={hasCounter}
                            endTracking={endTracking}
                            trackingContext={trackingContext}
                        />
                        <aside style={isShowGalleryBanner ? {} : { display: 'none' }}>
                            <AdSlot id={DisplayAdSlotDivNames.galleryBanner} />
                        </aside>
                    </>
                )}
            </Dialog>
            {!isDialogOpen && (
                <Carousel
                    slides={slidesWithAd}
                    breakpoints={breakpoints}
                    isFullScreen={isDialogOpen}
                    hasZoom
                    itemsToShow={1}
                    isDialogOpen={isDialogOpen}
                    hasCounterDots={hasCounterDots}
                    imageAltTitle={imageAltTitle}
                    openDialog={openDialog}
                    hasControls={hasControls}
                    slideNumber={slideNumber}
                    hasIcon={hasIcon}
                    hasCounter={hasCounter}
                    endTracking={endTracking}
                    showFullScreenOnClick
                    trackingContext={trackingContext}
                />
            )}
        </div>
    );
};

interface Props
    extends Pick<
        CarouselProps,
        | 'breakpoints'
        | 'isDialogOpen'
        | 'hasCounterDots'
        | 'imageAltTitle'
        | 'openDialog'
        | 'hasControls'
        | 'slideNumber'
        | 'hasIcon'
        | 'hasCounter'
        | 'endTracking'
        | 'trackingContext'
    > {
    closeDialog?(): any;
    mediaLinks: (ImageSlide | VideoSlide)[];
    isDialogOpen?: boolean;
}

CarouselGallery.defaultProps = {
    breakpoints: [],
    clickTracking: '',
    endTracking: '',
    hasControls: false,
    hasCounter: false,
    hasCounterDots: false,
    hasIcon: false,
    imageAltTitle: ',',
    imageUrls: [],
    isDialogOpen: false,
    keyTracking: '',
    slideNumber: 1,
    swipeTracking: '',
};

export default CarouselGallery;
